import { ToggleButton, Tooltip } from "@heroui/react";
import { StarIcon as StarIconOutline } from "@heroicons/react/24/outline";
import { FavoriteChip } from "./FavoriteChip";
import { useToggleFavorite } from "@/hooks/useToggleFavorite";

interface FavoriteToggleProps {
    recipeId: number;
    favorite: boolean;
    hasMadeIt: boolean;
}

/**
 * Favorite control for the recipe detail header.
 *
 * - Favorited → the accent "Favorite" chip, now pressable to un-favorite.
 * - Made but not yet favorited → an outline star inviting you to favorite it.
 * - Not yet made → nothing; you shouldn't favorite a recipe you haven't tried.
 */
export const FavoriteToggle = ({ recipeId, favorite, hasMadeIt }: FavoriteToggleProps) => {
    const toggleFavorite = useToggleFavorite();

    if (!favorite && !hasMadeIt) return null;

    const handleChange = (selected: boolean) => {
        toggleFavorite.mutate({ id: recipeId, favorite: selected });
    };

    if (favorite) {
        // Keep the existing Favorite chip; the ToggleButton just makes it
        // pressable (the `.favorite-chip-trigger` class strips its own box).
        return (
            <Tooltip delay={300}>
                <ToggleButton
                    isSelected
                    onChange={handleChange}
                    aria-label="Remove from favorites"
                    className="favorite-chip-trigger"
                >
                    <FavoriteChip />
                </ToggleButton>
                <Tooltip.Content>
                    <p>Remove from favorites</p>
                </Tooltip.Content>
            </Tooltip>
        );
    }

    return (
        <Tooltip delay={300}>
            <ToggleButton
                isIconOnly
                size="sm"
                isSelected={false}
                onChange={handleChange}
                aria-label="Add to favorites"
                className="cell-toggle--accent rounded-full"
            >
                <StarIconOutline strokeWidth={2} />
            </ToggleButton>
            <Tooltip.Content>
                <p>Add to favorites</p>
            </Tooltip.Content>
        </Tooltip>
    );
};
